import { useState, useEffect, useMemo, useRef } from 'react';

import debounce from 'lodash/debounce';
import { elasticApi, isBrowser, withURL } from '@lucid/utils';
import { navigate } from 'gatsby';

import type { Lucid } from '../types/types';
import useSiteMetadata from './use-site-metadata';
import useUrlPathUpdate from './use-url-path-update';
import useURLParams from './use-url-params';

export interface BlogSearchProps {
  /** The page's default posts for SSR */
  allPosts: Lucid.Directus.Generated.Blog_Post[];
  /** The page's default current page for SSR */
  ssrPage: number;
  /** The total number of pages */
  pageCount: number;
  /** The elastic engine language to search in */
  shortCode: Lucid.I18n.LanguagesShortCode;
  /** The elastic engine language to search in */
  urlLangPrefix: Lucid.I18n.LanguagesURLPrefix;
  /** The maximum number of search results to pull in */
  limit: number;
  /** The url slug to prefix generated url paths with */
  blogTaxonomySlug: string;
  /** The elastic engine to query for search results */
  elasticEngine: Lucid.Elastic.EngineName;
}

const useBlogSearch = ({
  allPosts,
  ssrPage,
  pageCount,
  shortCode,
  urlLangPrefix,
  limit,
  blogTaxonomySlug,
  elasticEngine,
}: BlogSearchProps) => {
  const { paramValue, updateURLParam } = useURLParams('s');
  const { product } = useSiteMetadata();
  const [posts, setPosts] = useState<Lucid.Directus.Generated.Blog_Post[]>(allPosts);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState<number>(ssrPage);
  const [totalSearchResults, setTotalSearchResults] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(pageCount);
  const [isQueryingElastic, setIsQueryingElastic] = useState(true);
  const [isLoading, setIsLoading] = useState(!!searchQuery);
  const postListRef = useRef<HTMLDivElement | HTMLInputElement | null>(null);

  useEffect(() => {
    setSearchQuery(paramValue);
  }, [paramValue]);

  useEffect(() => {
    if (isBrowser() && isLoading && paramValue && searchQuery) {
      postListRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  }, [isLoading, paramValue, searchQuery, page]);

  useUrlPathUpdate(
    withURL(
      `${blogTaxonomySlug !== '/' ? `${blogTaxonomySlug}` : ''}/${page && page > 1 ? `${page}` : ''}`.replace(
        /\/$/,
        '',
      ),
      {
        product,
        urlLangPrefix,
      },
    ),
  );

  const updateSearchResults = useMemo(
    () =>
      debounce((value: string) => {
        updateURLParam(value);
        setIsQueryingElastic(true);
        setSearchQuery(value);
        setPage(1);
      }, 800),
    [updateURLParam],
  );

  useEffect(() => {
    const fetchElasticResults = async () => {
      try {
        const response = await elasticApi.search({
          engine: elasticEngine,
          languageKey: shortCode,
          query: searchQuery.trim(),
          size: limit,
          page: page,
        });
        const elasticResults =
          response?.results?.map(result => ({
            id: result.id?.raw,
            image: { filename_disk: result.image?.raw } as Lucid.Directus.Generated.Directus_Files,
            translations: [
              {
                id: result.id?.raw,
                heading: result.title?.raw,
                path: { id: result.id?.raw, url: result.url?.raw },
                summary: result.summary?.raw || result.content.raw.slice(0, 200),
              },
            ],
          })) ?? [];
        setTotalSearchResults(response?.meta?.page?.total_results ?? 0);
        setTotalPages(response?.meta?.page?.total_pages ?? 1);
        setIsQueryingElastic(false);
        setPosts(elasticResults ?? []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    if (searchQuery.trim()) {
      fetchElasticResults();
    }
  }, [shortCode, searchQuery, limit, page, elasticEngine]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setPosts(allPosts);
      setTotalPages(pageCount);
    }
  }, [allPosts, pageCount, searchQuery]);

  useEffect(() => {
    if (!isQueryingElastic) {
      const loadingTimeout = setTimeout(() => setIsLoading(false), 800);

      return () => clearTimeout(loadingTimeout);
    }
  }, [isQueryingElastic, posts, page]);

  useEffect(() => {
    if (!paramValue) {
      setIsQueryingElastic(false);
    }
  }, [paramValue]);

  useEffect(() => {
    if (paramValue === searchQuery && !isQueryingElastic) {
      setIsLoading(false);
    }
  }, [isQueryingElastic, paramValue, searchQuery]);

  const handleSearch = (value: string) => {
    if (value !== searchQuery) {
      setIsLoading(true);
      updateSearchResults(value);
    }
  };

  const handleNewPage = (value: number) => {
    if (value === page) return;
    setIsLoading(true);

    const newPath = withURL(
      `${blogTaxonomySlug !== '/' ? `${blogTaxonomySlug}` : ''}/${value && value > 1 ? `${value}` : ''}`.replace(
        /\/$/,
        '',
      ),
      {
        product,
        urlLangPrefix,
        customParams: searchQuery ? [{ s: searchQuery }] : [],
      },
    );

    if (!searchQuery) {
      navigate(newPath);
      postListRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
    } else {
      setPage(value);
    }
  };

  return {
    posts,
    searchQuery,
    page,
    totalPages,
    isLoading,
    isQueryingElastic,
    handleSearch,
    handleNewPage,
    postListRef,
    totalSearchResults,
  };
};

export default useBlogSearch;
